import {createManholeGeometry} from "./manholeGeometry";
import {getClosureSizes} from "../../../../domain/manhole";
import {dash, renderLine} from "../../renderFunctions";
import {Group, Shape} from "react-konva";
import React from "react";
import {Plan, Side, Manhole} from "types";
import {LidGeometry} from "./lidGeometry";

interface SubFloorManholeProps {
    plan: Plan,
    scale: number,
    side: Side,
    manhole: Manhole,
    lidGeometry: LidGeometry
}

export const SubFloorManhole: React.FC<SubFloorManholeProps> = ({plan, scale, side, manhole, lidGeometry}) => {

    const manholeGeometry = createManholeGeometry(plan, manhole, scale, side, lidGeometry);
    const getClosuresBottom = () => lidGeometry.bottom
    const getClosuresHeight = () => getClosureSizes(manhole.type).thickness * scale
    const getClosuresTop = () => getClosuresBottom() - getClosuresHeight()


    const renderManholeOutline = () => {
        return renderLine([
            manholeGeometry.clearLeft(), getClosuresTop(),
            manholeGeometry.clearLeft(), getClosuresBottom(),
            manholeGeometry.clearRight(), getClosuresBottom(),
            manholeGeometry.clearRight(), getClosuresTop(),
            manholeGeometry.clearLeft(), getClosuresTop()
        ])
    }
    const renderClosuresShortSide = () => {
        const getClosureWidth = () => getClosureSizes(manhole.type).width
        const renderClosuresIntersections = () => {
            return manholeGeometry.closureIntersectionPositions()
                .map((p, i) => renderLine([p, getClosuresTop(), p, getClosuresBottom()], 'divider_' + i))
        }
        const renderHoldingBrackets = () => {
            const getBracketDiameter = () => (getClosureSizes(manhole.type).bracketDiameter ?? 0) * scale
            const getBracketRadius = () => (getClosureSizes(manhole.type).bracketRadius ?? 0) * scale
            const getOuterRadius = () => getBracketRadius() + getBracketDiameter() / 2
            const getInnerRadius = () => getBracketRadius() - getBracketDiameter() / 2
            const getHeight = () =>
                (getClosureSizes(manhole.type).bracketHeight ?? 0) * scale // bottom to top center distance
            const getWidth = () =>
                (getClosureSizes(manhole.type).bracketWidth ?? 0) * scale // center to center distance
            const getLeftCenter = () => ({
                x: -getWidth() / 2 + getOuterRadius(),
                y: -(getHeight() - getOuterRadius() + getBracketDiameter() / 2)
            })
            const getRightCenter = () => ({...getLeftCenter(), x: -getLeftCenter().x})

            manholeGeometry.closureIntersectionPositions()
                .map((p, i) => renderLine([p, getClosuresTop(), p, getClosuresBottom()], 'divider_' + i))
            return [
                manholeGeometry.clearLeft(),
                ...manholeGeometry.closureIntersectionPositions()
            ]
                .map(p => p + getClosureWidth() / 2 * scale)
                .map((p, i) =>
                    <Group x={p} y={getClosuresTop()} key={'bracket_' + i}>
                        <Shape
                            sceneFunc={(context, shape) => {
                                context.beginPath();
                                context.moveTo(getLeftCenter().x - getOuterRadius(), 0)
                                context.lineTo(getLeftCenter().x - getOuterRadius(), getLeftCenter().y)
                                context.arc(
                                    getLeftCenter().x,
                                    getLeftCenter().y,
                                    getOuterRadius(),
                                    Math.PI,
                                    -Math.PI / 2,
                                    false
                                );
                                context.arc(
                                    getRightCenter().x,
                                    getRightCenter().y,
                                    getOuterRadius(),
                                    -Math.PI / 2,
                                    0,
                                    false
                                );
                                context.lineTo(getRightCenter().x + getOuterRadius(), 0)
                                context.lineTo(getRightCenter().x + getInnerRadius(), 0)
                                context.arc(
                                    getRightCenter().x,
                                    getRightCenter().y,
                                    getInnerRadius(),
                                    0,
                                    -Math.PI / 2,
                                    true
                                );
                                context.arc(
                                    getLeftCenter().x,
                                    getLeftCenter().y,
                                    getInnerRadius(),
                                    -Math.PI / 2,
                                    Math.PI,
                                    true
                                );
                                context.lineTo(getLeftCenter().x - getInnerRadius(), 0)
                                context.closePath();
                                context.fillStrokeShape(shape);
                            }}
                            fill={'black'}
                            stroke="black"
                            strokeWidth={1}
                        />
                    </Group>
                )
        }
        return (
            <React.Fragment>
                {renderManholeOutline()}
                {renderClosuresIntersections()}
                {renderHoldingBrackets()}
            </React.Fragment>
        )
    }
    const renderClosuresLongSide = () => {
        const renderManhole = () => {
            const getBracketSpacing = () => (getClosureSizes(manhole.type).bracketPosition ?? 0) * scale
            const getLeftBracketX = () => manholeGeometry.clearLeft() + getBracketSpacing()
            const getRightBracketX = () => manholeGeometry.clearRight() - getBracketSpacing()
            const renderHoldingBracket = (x: number, y: number, renderDashed = false) => {
                const getDiameter = () => (getClosureSizes(manhole.type).bracketDiameter ?? 0) * scale
                const getRadius = () => getDiameter() / 2
                const getHeight = () => (getClosureSizes(manhole.type).bracketHeight ?? 0) * scale
                const getCenterY = () => -getHeight()
                const getStroke = () => renderDashed ? 'black' : 'transparent'
                const getFill = () => renderDashed ? 'transparent' : 'black'

                return (
                    <Group x={x} y={y}>
                        <Shape
                            sceneFunc={(context, shape) => {
                                context.beginPath();
                                context.moveTo(-getRadius(), 0)
                                context.arc(0, getCenterY(), getRadius(), Math.PI, 0, false);
                                context.lineTo(getRadius(), 0)

                                context.closePath();
                                context.fillStrokeShape(shape);
                            }}
                            fill={getFill()}
                            stroke={getStroke()}
                            strokeWidth={1}
                            dash={dash}
                        />
                    </Group>
                )
            }
            const render = () => {
                return <React.Fragment>
                    {
                        renderLine([
                            manholeGeometry.clearLeft(), getClosuresBottom(),
                            manholeGeometry.clearLeft(), getClosuresTop(),
                            manholeGeometry.clearRight(), getClosuresTop(),
                            manholeGeometry.clearRight(), getClosuresBottom(),
                            manholeGeometry.clearLeft(), getClosuresBottom()
                        ])
                    }
                    {renderHoldingBracket(getLeftBracketX(), getClosuresTop())}
                    {renderHoldingBracket(getRightBracketX(), getClosuresTop())}
                </React.Fragment>
            }
            return render()
        }

        return (
            <React.Fragment>
                {!!manhole && renderManhole()}
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            {manholeGeometry.isClosuresShortSide() && renderClosuresShortSide()}
            {manholeGeometry.isClosuresLongSide() && renderClosuresLongSide()}
        </React.Fragment>
    )
}
